import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { arcadeAudio } from "./audio";
import { createWebcamMappedProfile, migrateCalibrationProfile, resetControlSettings } from "./controlDefaults";
import { useFaceControls } from "./input/useFaceControls";
import { MainMenu, MovementWarmup, OnboardingScreen, ResultScreen, SettingsPanel } from "./screens";
import { resolveEntryRoute } from "./entryRoute";
import type { CalibrationProfile, GameResult, TriggerMode } from "./types";

type Screen = "menu" | "onboarding" | "practice" | "game" | "result";

const GameScreen = lazy(() =>
  import("./game/GameScene").then((module) => ({ default: module.GameScreen })),
);

// Keep the legacy storage key so existing Admin settings survive the move to automatic mapping.
const CONTROL_PROFILE_KEY = "bombs-away-calibration-v1";
const SETTINGS_KEY = "bombs-away-settings-v1";

function loadProfile(): CalibrationProfile {
  try {
    const value = localStorage.getItem(CONTROL_PROFILE_KEY);
    const parsed = value ? JSON.parse(value) : null;
    const saved = migrateCalibrationProfile(parsed);
    const mapped = createWebcamMappedProfile(saved?.trigger ?? "mouth");
    return saved
      ? { ...saved, neutral: mapped.neutral, range: mapped.range, calibratedAt: mapped.calibratedAt }
      : mapped;
  } catch {
    return createWebcamMappedProfile();
  }
}

function App() {
  const entryRoute = resolveEntryRoute(window.location.pathname);
  const savedSettings = (() => {
    try {
      return JSON.parse(localStorage.getItem(SETTINGS_KEY) ?? "{}") as {
        muted?: boolean;
        reducedMotion?: boolean;
        trigger?: TriggerMode;
      };
    } catch {
      return {};
    }
  })();

  const [screen, setScreen] = useState<Screen>("menu");
  const [profile, setProfile] = useState<CalibrationProfile>(loadProfile);
  const [trigger, setTrigger] = useState<TriggerMode>(savedSettings.trigger ?? profile.trigger ?? "mouth");
  const [muted, setMuted] = useState(Boolean(savedSettings.muted));
  // This head-tracked game depends on continuous interpolation. Keep full motion on
  // unless the player explicitly disables it in Admin settings.
  const [reducedMotion, setReducedMotion] = useState(savedSettings.reducedMotion ?? false);
  const [settingsOpen, setSettingsOpen] = useState(entryRoute === "control-lab");
  const [demoMode, setDemoMode] = useState(false);
  const [result, setResult] = useState<GameResult | null>(null);
  const controls = useFaceControls(profile, trigger);
  const trackingLockedRef = useRef(false);

  useEffect(() => {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify({ muted, reducedMotion, trigger }));
    arcadeAudio.setMuted(muted);
  }, [muted, reducedMotion, trigger]);

  useEffect(() => {
    if (entryRoute !== "control-lab") return;

    const previousTitle = document.title;
    const existing = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    const meta = existing ?? document.createElement("meta");
    const previousContent = existing?.content;
    if (!existing) {
      meta.name = "robots";
      document.head.append(meta);
    }
    meta.content = "noindex, nofollow, noarchive";
    document.title = "Control Lab · Bombs Away!";

    return () => {
      document.title = previousTitle;
      if (existing) meta.content = previousContent ?? "";
      else meta.remove();
    };
  }, [entryRoute]);

  useEffect(() => {
    const cameraActive = screen === "practice" || (!demoMode && screen === "game") || settingsOpen;
    const searching = controls.status === "requesting" || controls.status === "loading-model" || controls.status === "lost";
    const locked = controls.status === "tracking";

    if (!cameraActive || controls.status === "idle" || controls.status === "denied" || controls.status === "error") {
      arcadeAudio.stopTrackingBuzz();
      trackingLockedRef.current = false;
      return;
    }

    if (locked) {
      arcadeAudio.stopTrackingBuzz();
      if (!trackingLockedRef.current) arcadeAudio.playTrackingLock();
      trackingLockedRef.current = true;
      return;
    }

    if (searching) {
      trackingLockedRef.current = false;
      arcadeAudio.startTrackingBuzz();
    }
  }, [controls.status, demoMode, muted, screen, settingsOpen]);

  const beginWarmup = async () => {
    arcadeAudio.unlock();
    setDemoMode(false);
    setResult(null);
    await controls.startCamera();
    setScreen("practice");
  };

  const beginDemo = () => {
    arcadeAudio.unlock();
    setDemoMode(true);
    setResult(null);
    setScreen("game");
  };

  const changeTrigger = (nextTrigger: TriggerMode) => {
    setTrigger(nextTrigger);
    setProfile((current) => ({ ...current, trigger: nextTrigger }));
  };

  const closeControlLab = () => {
    setSettingsOpen(false);
    if (screen === "menu") controls.stopCamera();
  };

  const saveControlDefaults = () => {
    const next = { ...profile, trigger };
    setProfile(next);
    localStorage.setItem(CONTROL_PROFILE_KEY, JSON.stringify(next));
  };

  const restoreRecommendedDefaults = () => {
    setProfile(resetControlSettings(profile));
  };

  const showResult = (next: GameResult) => {
    setResult(next);
    controls.stopCamera();
    setScreen("result");
  };

  const replay = async () => {
    setResult(null);
    if (!demoMode) await controls.startCamera();
    setScreen("game");
  };

  const goMenu = () => {
    controls.stopCamera();
    setScreen("menu");
  };

  if (entryRoute === "blocked-review") {
    return (
      <main className="route-not-found">
        <strong>404</strong>
        <h1>Nothing to review here.</h1>
        <a className="button button--primary" href={import.meta.env.BASE_URL}>Play Bombs Away!</a>
      </main>
    );
  }

  return (
    <div className="app-shell">
      {screen === "menu" && (
        <MainMenu
          onPlay={() => setScreen("onboarding")}
          onDemo={beginDemo}
        />
      )}

      {screen === "onboarding" && (
        <OnboardingScreen onContinue={() => void beginWarmup()} onBack={goMenu} />
      )}

      {screen === "practice" && (
        <MovementWarmup
          status={controls.status}
          error={controls.error}
          pose={controls.pose}
          profile={profile}
          reducedMotion={reducedMotion}
          setVideoElement={controls.setVideoElement}
          startCamera={controls.startCamera}
          onComplete={() => setScreen("game")}
          onBack={goMenu}
        />
      )}

      {screen === "game" && (
        <>
          {!demoMode && <video ref={controls.setVideoElement} className="sensor-video" muted playsInline />}
          <Suspense fallback={<div className="game-loader"><span className="loader" /><strong>Charging laser system…</strong></div>}>
            <GameScreen
              pose={controls.pose}
              controlProfile={profile}
              trackingStatus={controls.status}
              fireSignal={controls.fireSignal}
              lastTrigger={controls.lastTrigger}
              demoMode={demoMode}
              muted={muted}
              reducedMotion={reducedMotion}
              onMutedChange={setMuted}
              onExit={goMenu}
              onResult={showResult}
            />
          </Suspense>
        </>
      )}

      {screen === "result" && result && <ResultScreen result={result} onReplay={() => void replay()} onMenu={goMenu} />}

      <SettingsPanel
        open={settingsOpen}
        muted={muted}
        reducedMotion={reducedMotion}
        trigger={trigger}
        voiceSupported={controls.voiceSupported}
        profile={profile}
        status={controls.status}
        pose={controls.pose}
        blend={controls.blend}
        fireSignal={controls.fireSignal}
        setVideoElement={controls.setVideoElement}
        startCamera={controls.startCamera}
        onMuted={setMuted}
        onReducedMotion={setReducedMotion}
        onTrigger={changeTrigger}
        onProfileChange={setProfile}
        onSaveDefaults={saveControlDefaults}
        onResetDefaults={restoreRecommendedDefaults}
        onClose={closeControlLab}
        gamePreview={(
          <Suspense fallback={<div className="game-loader"><span className="loader" /><strong>Starting live sandboxâ€¦</strong></div>}>
            <GameScreen
              pose={controls.pose}
              controlProfile={profile}
              trackingStatus={controls.status}
              fireSignal={controls.fireSignal}
              lastTrigger={controls.lastTrigger}
              demoMode={false}
              muted={muted}
              reducedMotion={reducedMotion}
              onMutedChange={setMuted}
              onExit={closeControlLab}
              onResult={() => undefined}
              sandboxMode
            />
          </Suspense>
        )}
      />
    </div>
  );
}

export default App;
